<template>
	<ModalV2
		:value="true"
		size="md"
		labelled-by="review-documents-title"
		:close-on-backdrop="!isBusy"
		:close-on-esc="!isBusy"
		panel-class="max-w-[650px] rounded-xl border border-grey-200 shadow-xl"
		body-class="review-documents-body flex max-h-[calc(100vh-13rem)] flex-col gap-5 px-6 pb-5 pt-4"
		:max-body-vh="0"
		footer-class="flex-row items-center justify-between border-t border-grey-200 bg-white px-6 py-4 md:justify-between"
		@input="close"
	>
		<template #header>
			<div
				class="flex items-center gap-4 border-b border-grey-200 p-6 text-grey-1000"
			>
				<div class="flex min-w-0 flex-1 flex-col gap-1">
					<h2
						id="review-documents-title"
						class="mb-0 font-sans text-base font-semibold normal-case"
					>
						{{ $t('application.review-documents.title') }}
					</h2>
					<p
						v-if="subtitleParts.length"
						class="mb-0 text-sm text-grey-700"
					>
						<span
							v-for="(part, index) in subtitleParts"
							:key="index"
							:class="{ 'text-grey-1000': part.highlight }"
							>{{ part.text }}</span
						>
					</p>
				</div>
				<button
					type="button"
					class="rounded-lg p-2 disabled:opacity-50"
					:aria-label="$t('crud.close')"
					:disabled="!canClose"
					@click="close"
				>
					<IconTimes :size="16" />
				</button>
			</div>
		</template>

		<div class="flex gap-3 rounded-lg bg-info-subtle p-4 text-[#294451]">
			<IconInfoCircle :size="20" class="shrink-0" />
			<div class="flex flex-col gap-0.5 text-sm">
				<p class="mb-0 font-medium">
					{{ $t('application.review-documents.shared.title') }}
				</p>
				<p id="review-documents-shared-message" class="mb-0">
					{{ $t('application.review-documents.shared.message') }}
				</p>
			</div>
		</div>

		<div
			v-if="loadFailed"
			class="flex items-center gap-3 rounded-lg border border-grey-300 bg-grey-25 px-4 py-3 text-base text-grey-1000"
			role="alert"
			data-test="review-load-error"
		>
			<span class="min-w-0 flex-1">
				{{ $t('application.review-documents.load-failed') }}
			</span>
			<Button
				type="secondary"
				data-test="review-retry"
				:disabled="isBusy"
				@click="fetchAttachments"
			>
				{{ $t('application.review-documents.retry') }}
			</Button>
		</div>
		<p
			v-else-if="!hasLoaded"
			class="mb-0 text-base text-grey-700"
			role="status"
			data-test="review-loading"
		>
			{{ $t('general.loading') }}…
		</p>
		<template v-else>
			<section class="flex flex-col gap-3">
				<h3
					class="mb-0 font-heading text-base font-black uppercase text-grey-1000"
				>
					{{ $t('application.review-documents.cv') }}
					<span class="text-error">*</span>
				</h3>
				<div v-if="cv" :class="rowClass" data-test="review-cv">
					<span class="min-w-0 flex-1 truncate pr-1 font-normal">
						{{ fileName(cv) }}
					</span>
					<button
						type="button"
						:class="iconButtonClass"
						:aria-label="$t('application.review-documents.view')"
						@click="view(cv)"
					>
						<IconEye :size="20" />
					</button>
					<button
						type="button"
						class="h-10 rounded-lg px-3.5 text-base text-grey-1000 transition-colors duration-200 hover:bg-grey-100 active:bg-[#E5E5E5] disabled:opacity-50 disabled:hover:bg-transparent"
						data-test="review-cv-replace"
						:disabled="isBusy"
						@click="$refs.cvInput.click()"
					>
						{{ $t('application.review-documents.replace') }}
					</button>
				</div>
				<!-- An empty slot needs its own upload: Submit requires a CV and extra documents never fill the slot. -->
				<button
					v-else
					type="button"
					class="flex w-full items-center gap-3 rounded-lg border-2 border-dashed border-[#e3e3e3] p-4 text-base text-grey-700 disabled:opacity-50"
					data-test="review-cv-upload"
					:disabled="isBusy"
					@click="$refs.cvInput.click()"
				>
					<IconPlus :size="16" />
					{{ $t('application.review-documents.cv') }}
				</button>
				<input
					ref="cvInput"
					type="file"
					accept="application/pdf,.pdf"
					:aria-label="$t('application.review-documents.cv')"
					aria-describedby="review-documents-shared-message"
					class="hidden"
					data-test="review-cv-input"
					@change="replaceCv"
				/>
			</section>

			<section class="flex flex-col gap-3">
				<div class="flex flex-col gap-0.5">
					<h3
						class="mb-0 font-heading text-base font-black uppercase text-grey-1000"
					>
						{{
							$t('application.review-documents.additional.title')
						}}
					</h3>
					<p
						id="review-documents-additional-hint"
						class="mb-0 text-sm text-grey-700"
					>
						{{ $t('application.review-documents.additional.hint') }}
					</p>
				</div>
				<div
					v-for="document in documents"
					:key="document.id"
					:class="rowClass"
					data-test="review-document"
				>
					<span class="min-w-0 flex-1 truncate pr-1 font-normal">
						{{ fileName(document) }}
					</span>
					<button
						type="button"
						:class="iconButtonClass"
						:aria-label="$t('application.review-documents.view')"
						@click="view(document)"
					>
						<IconEye :size="20" />
					</button>
					<button
						type="button"
						:class="removeButtonClass"
						:aria-label="$t('application.review-documents.remove')"
						data-test="review-document-remove"
						:disabled="isBusy"
						@click="removeDocument(document)"
					>
						<IconTrash :size="20" />
					</button>
				</div>
				<button
					type="button"
					class="flex items-center gap-3 rounded-lg w-full border-2 border-dashed border-[#e3e3e3] p-4 text-base text-grey-700 disabled:opacity-50"
					data-test="review-add-document"
					:disabled="isBusy"
					@click="$refs.documentInput.click()"
				>
					<IconPlus :size="16" />
					{{ $t('application.review-documents.additional.add') }}
				</button>
				<input
					ref="documentInput"
					type="file"
					accept="application/pdf,.pdf"
					multiple
					:aria-label="
						$t('application.review-documents.additional.add')
					"
					aria-describedby="review-documents-additional-hint"
					class="hidden"
					data-test="review-document-input"
					@change="addDocuments"
				/>
			</section>
		</template>

		<template #footer>
			<Button type="secondary" :disabled="!canClose" @click="close">
				{{ $t('crud.cancel') }}
			</Button>
			<Button
				type="primary"
				data-test="review-submit"
				:disabled="!cv || isBusy || loadFailed"
				@click="$emit('submit')"
			>
				{{ $t('application.submit-application') }}
			</Button>
		</template>
	</ModalV2>
</template>

<script>
import ModalV2 from '~/components/Modal/ModalV2'
import Button from '~/components/ui/common/Button'
import {
	IconEye,
	IconInfoCircle,
	IconPlus,
	IconTimes,
	IconTrash
} from '~/components/Icons'
import { findLatestCvSlot, isCvSlotMedia } from '~/utils/cvMedia'
import { isPdfFile } from '~/utils/fileType'

const MAX_FILE_SIZE = 20 * 1024 * 1024

export default {
	name: 'ReviewDocumentsModal',
	components: {
		ModalV2,
		Button,
		IconEye,
		IconInfoCircle,
		IconPlus,
		IconTimes,
		IconTrash
	},
	props: {
		applicantProfileId: { type: [Number, String], required: true },
		jobOfferName: { type: String, default: '' },
		companyName: { type: String, default: '' },
		submitting: { type: Boolean, default: false }
	},
	data() {
		return {
			uploading: false,
			// A failed reload must not leave a stale list submittable (the backend may have deleted it).
			loadFailed: false,
			loading: false,
			hasLoaded: false,
			// Only the newest request may write the list; an older response arriving late is stale.
			latestRequest: 0,
			rowClass:
				'flex items-center gap-2 rounded-lg border border-grey-300 bg-grey-25 px-4 py-3 text-base text-grey-1000',
			iconButtonClass:
				'rounded-lg p-2.5 text-grey-1000 transition-colors duration-200 hover:bg-grey-100 active:bg-[#E5E5E5] disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-grey-1000',
			removeButtonClass:
				'rounded-lg p-2.5 text-grey-1000 transition-colors duration-200 hover:bg-[#FEE4E2] hover:text-destructive active:bg-[#FDD6CE] disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-grey-1000'
		}
	},
	computed: {
		attachments() {
			return this.$store.getters['applicantProfile/attachments'](
				this.applicantProfileId
			)
		},
		// Organisations see the newest CV-slot file; the backend list already applies that rule.
		cv() {
			return findLatestCvSlot(this.attachments)
		},
		documents() {
			return this.attachments.filter(media => !isCvSlotMedia(media))
		},
		isBusy() {
			return this.uploading || this.submitting || this.loading
		},
		// A pending upload would land after submit if the modal were closed and reopened.
		canClose() {
			return !this.uploading && !this.submitting
		},
		// Splits the translated sentence so job and company can be highlighted in any word order.
		subtitleParts() {
			if (!this.jobOfferName || !this.companyName) return []

			const marker = '\u0000'
			const values = { job: this.jobOfferName, company: this.companyName }
			return this.$t('application.review-documents.subtitle', {
				job: `${marker}job${marker}`,
				company: `${marker}company${marker}`
			})
				.split(marker)
				.filter(Boolean)
				.map(text =>
					values[text]
						? { text: values[text], highlight: true }
						: { text, highlight: false }
				)
		},
		endpoint() {
			return `/applicant-profile/${this.applicantProfileId}`
		}
	},
	mounted() {
		this.fetchAttachments()
	},
	methods: {
		async fetchAttachments() {
			const request = ++this.latestRequest
			this.loading = true
			try {
				const loaded = await this.$store.dispatch(
					'applicantProfile/fetchAttachments',
					this.applicantProfileId
				)
				if (request !== this.latestRequest) return
				this.loadFailed = !loaded
				this.hasLoaded = loaded
			} catch (error) {
				if (request === this.latestRequest) this.loadFailed = true
			} finally {
				if (request === this.latestRequest) this.loading = false
			}
		},
		fileName(media) {
			return media.file_name || media.name
		},
		view(media) {
			window.open(media.url, '_blank', 'noopener')
		},
		close() {
			if (this.canClose) this.$emit('close')
		},
		validFile(file) {
			if (!isPdfFile(file)) {
				this.toastError(file, 'documents.resume.error.invalid_type')
				return false
			}
			if (file.size > MAX_FILE_SIZE) {
				this.toastError(file, 'documents.resume.error.file_too_large')
				return false
			}
			return true
		},
		toastError(file, key) {
			this.$store.dispatch('toast/push', {
				title: `${file.name}: ${this.$t(key)}`,
				type: 'error'
			})
		},
		upload(file, payload) {
			return this.$store.dispatch('media/upload', {
				file,
				endpoint: `${this.endpoint}/attachment`,
				field: 'attachment',
				options: {
					contentType: 'application/pdf',
					payload: { collection_name: 'attachment', ...payload }
				}
			})
		},
		async withUploading(files, callback) {
			this.uploading = true
			try {
				for (const file of files) {
					if (!this.validFile(file)) continue
					try {
						await callback(file)
					} catch (error) {
						this.toastError(
							file,
							'documents.additional.error.upload_failed'
						)
					}
				}
				await this.fetchAttachments()
			} finally {
				this.uploading = false
			}
		},
		// Never calls cv-enrichment: replacing only changes what organisations see.
		replaceCv(event) {
			const files = Array.from(event.target.files || []).slice(0, 1)
			event.target.value = ''
			return this.withUploading(files, file =>
				this.upload(file, { is_shared_cv: '1' })
			)
		},
		addDocuments(event) {
			const files = Array.from(event.target.files || [])
			event.target.value = ''
			return this.withUploading(files, file => this.upload(file, {}))
		},
		async removeDocument(media) {
			this.uploading = true
			try {
				await this.$store.dispatch(
					'applicantProfile/deleteAttachment',
					{
						applicantProfileId: this.applicantProfileId,
						attachmentId: media.id
					}
				)
				await this.fetchAttachments()
			} catch (error) {
				this.$store.dispatch('toast/push', {
					title: this.$t('documents.additional.error.upload_failed'),
					type: 'error'
				})
			} finally {
				this.uploading = false
			}
		}
	}
}
</script>

<style scoped>
/* Figma scroll thumb: 8px grey-400, 4px radius, 2px from the panel edge. */
/* Chrome ignores ::-webkit-scrollbar once the standard properties are set, so keep them Firefox-only. */
@supports (-moz-appearance: none) {
	::v-deep .review-documents-body {
		scrollbar-width: thin;
		scrollbar-color: #b9bdc0 transparent;
	}
}

::v-deep .review-documents-body::-webkit-scrollbar {
	width: 12px;
}

::v-deep .review-documents-body::-webkit-scrollbar-thumb {
	border: 2px solid transparent;
	border-radius: 6px;
	background-color: #b9bdc0;
	background-clip: padding-box;
}
</style>
